import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CompanyService } from 'src/app/core/services/company.service';
import { Company } from 'src/app/core/models/company';
import { BaseComponent } from '@stockaid/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductAdsService } from 'src/app/core/services/product-ads.service';
import { ChangeDetectorRef } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ProductAdsActionService } from 'src/app/routes/dashboard/amz-advertising/services/product-ads-action.service';
import { ManagedScope } from 'src/app/core/models/product-ads';
import { buildActionCenterFilter } from 'src/app/routes/dashboard/amz-advertising/helper/default-filter';
import { ProductAdsBulkReviewModalComponent } from 'src/app/routes/dashboard/amz-advertising/campaign-product-ads/product-ads-bulk-review-modal/product-ads-bulk-review-modal.component';
import { from, EMPTY } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

export type RiskType = 'high-risk' | 'moderate-risk' | 'low-risk' | 'neutral';

interface ActionRiskCard {
  id: string;
  title: string;
  tooltip: string;
  count: number;
  type: RiskType;
  primaryAction: 'PAUSE' | 'ENABLE';
  primaryActionLabel: string;
  viewActionLabel: string;
  isSelected: boolean;
  icon: string;
  wellStockedManaged: number;
  wellStockedExternal: number;
}

@Component({
  selector: 'app-action-center-manual-suggestion',
  templateUrl: './action-center-manual-suggestion.component.html',
  styleUrls: ['./action-center-manual-suggestion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionCenterManualSuggestionComponent extends BaseComponent implements OnInit {
  isCollapsed = false;
  currentCompany: Company;
  isLoading = true;
  public readonly headerTooltip = 'Summary of SKUs that currently need manual advertising action.';
  risks: ActionRiskCard[] = [
    {
      id: 'active-high',
      title: 'Active - High Risk SKU(s)',
      tooltip: 'Active SKUs with High Risk inventory status that may require immediate ad pause.',
      count: 0,
      type: 'high-risk',
      primaryAction: 'PAUSE',
      primaryActionLabel: 'Pause All',
      viewActionLabel: 'View',
      isSelected: false,
      icon: 'fas fa-exclamation-triangle',
      wellStockedManaged: 0,
      wellStockedExternal: 0
    },
    {
      id: 'active-mod',
      title: 'Active - Moderate Risk SKU(s)',
      tooltip: 'Active SKUs with Moderate Risk inventory status that may need attention before stockout risk increases.',
      count: 0,
      type: 'moderate-risk',
      primaryAction: 'PAUSE',
      primaryActionLabel: 'Pause All',
      viewActionLabel: 'View',
      isSelected: false,
      icon: 'fas fa-info-circle',
      wellStockedManaged: 0,
      wellStockedExternal: 0
    },
    {
      id: 'paused-mod',
      title: 'Paused - Moderate Risk SKU(s)',
      tooltip: 'Paused SKUs with Moderate Risk inventory status that may be eligible to remain paused or re-enabled.',
      count: 0,
      type: 'moderate-risk',
      primaryAction: 'ENABLE',
      primaryActionLabel: 'Enable All',
      viewActionLabel: 'View',
      isSelected: false,
      icon: 'fas fa-play',
      wellStockedManaged: 0,
      wellStockedExternal: 0
    },
    {
      id: 'paused-well',
      title: 'Paused - Well Stocked SKU(s)',
      tooltip: 'Paused SKUs with Well Stocked inventory status that may be ready to be re-enabled.',
      count: 0,
      type: 'low-risk',
      primaryAction: 'ENABLE',
      primaryActionLabel: 'Enable All',
      viewActionLabel: 'View',
      isSelected: false,
      icon: 'fas fa-play',
      wellStockedManaged: 0,
      wellStockedExternal: 0
    }
  ];

  constructor(
    private companyService: CompanyService,
    private modalService: NgbModal,
    private productAdsService: ProductAdsService,
    private cdr: ChangeDetectorRef,
    private actionService: ProductAdsActionService
  ) {
    super();
  }

  ngOnInit(): void {
    this.companyService.getCurrentCompany().pipe(this.autoCleanUp()).subscribe((company) => {
      this.currentCompany = company;
      
      // If no access, interaction is disabled and body is hidden
      this.loadStats();
      this.cdr.markForCheck();
    });

    // Sync with global bulk loading state (shows skeletons during background updates)
    this.actionService.isBulkLoading$
      .pipe(this.autoCleanUp())
      .subscribe((loading) => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      });

    // Reactive sync: When an action succeeds anywhere in the app, refresh counts
    this.actionService.actionSuccess$
      .pipe(this.autoCleanUp())
      .subscribe(() => {
        this.loadStats();
      });
  }

  loadStats(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.productAdsService.getActionCenterStats().pipe(
      this.autoCleanUp(),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe((stats) => {
      this.risks = this.risks.map(risk => {
        let count = 0;
        const extra: Partial<ActionRiskCard> = {};
        switch (risk.id) {
          case 'active-high':
            extra.wellStockedManaged = stats.activeHighRiskManaged ?? 0;
            extra.wellStockedExternal = stats.activeHighRiskExternal ?? 0;
            count = stats.activeHighRisk;
            break;
          case 'active-mod':
            extra.wellStockedManaged = stats.activeModerateRiskManaged ?? 0;
            extra.wellStockedExternal = stats.activeModerateRiskExternal ?? 0;
            count = stats.activeModerateRisk;
            break;
          case 'paused-mod':
            extra.wellStockedManaged = stats.pausedModerateManaged ?? 0;
            extra.wellStockedExternal = stats.pausedModerateExternal ?? 0;
            count = (extra.wellStockedManaged as number) + (extra.wellStockedExternal as number);
            break;
          case 'paused-well':
            extra.wellStockedManaged = stats.pausedWellStockedManaged ?? 0;
            extra.wellStockedExternal = stats.pausedWellStockedExternal ?? 0;
            count = (extra.wellStockedManaged as number) + (extra.wellStockedExternal as number);
            break;
        }
        return { ...risk, count, ...extra };
      });
    });
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  toggleCardSelection(card: ActionRiskCard): void {
    if (card.count === 0) {
      return;
    }

    const wasSelected = card.isSelected;
    
    // Deselect all cards
    this.risks.forEach(r => r.isSelected = false);
    
    // Toggle the clicked card (if it wasn't already selected)
    card.isSelected = !wasSelected;
  }

  onPrimaryAction(event: MouseEvent, card: ActionRiskCard): void {
    this.onPrimaryActionScoped(event, card, undefined);
  }

  onPrimaryActionScoped(event: MouseEvent, card: ActionRiskCard, scope: 'managed' | 'external' | 'all' | undefined): void {
    event.stopPropagation();
    const count = scope === 'managed' ? (card.wellStockedManaged ?? card.count)
                : scope === 'external' ? (card.wellStockedExternal ?? card.count)
                : card.count;
    if (count === 0) return;

    // Open confirmation modal
    const isPause = card.primaryActionLabel.toLowerCase().includes('pause');
    const isEnabling = !isPause;

    // Open bulk review modal
    const modalRef = this.modalService.open(ProductAdsBulkReviewModalComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      centered: true,
      scrollable: true,
    });

    const riskId = (scope as any) === 'managed' ? `${card.id}-managed`
                 : (scope as any) === 'external' ? `${card.id}-external`
                 : card.id;

    Object.assign(modalRef.componentInstance, {
      action: 'active',
      targetValue: isEnabling,
      isSelectAll: true,
      filter: buildActionCenterFilter(riskId),
      managedScope: (scope as any) || ManagedScope.ALL,
      totalRootCount: card.count,
    });

    from(modalRef.result).pipe(
      switchMap(() => EMPTY),
      catchError(() => EMPTY)
    ).subscribe();
  }

  onViewActionScoped(event: MouseEvent, card: ActionRiskCard, scope: 'managed' | 'external' | 'all'): void {
    event.stopPropagation();
    const count = scope === 'managed' ? (card.wellStockedManaged ?? 0)
                : scope === 'external' ? (card.wellStockedExternal ?? 0)
                : card.count;
    if (count === 0) return;
    const riskId = scope === 'managed' ? `${card.id}-managed`
                 : scope === 'external' ? `${card.id}-external`
                 : card.id;
    const filter = buildActionCenterFilter(riskId);
    this.actionService.requestActionCenterView(filter);
  }

  onViewAction(event: MouseEvent, card: ActionRiskCard): void {
    event.stopPropagation();
    if (card.count === 0) return;

    // Push the filter directly to the treelist via the shared service bus.
    // No URL mutation — the treelist is already on the same page.
    const filter = buildActionCenterFilter(card.id);
    this.actionService.requestActionCenterView(filter);
  }

  get totalRisks(): number {
    return this.risks.reduce((acc, risk) => acc + risk.count, 0);
  }

  trackByRisks(index: number, item: ActionRiskCard): string {
    return item.id;
  }
}
